import { useState, useEffect, useRef, type ComponentType, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { LocaleLink, LocaleNavLink } from "../i18n/LocaleLink";
import { LazyMotion, domAnimation, m, AnimatePresence } from "motion/react";
import {
  Clock, ChevronDown, Menu, X, Timer, Car, LayoutDashboard, Plane,
  ShieldCheck, Rocket, BookOpen, PlayCircle, HelpCircle, ArrowRight,
} from "lucide-react";
import { Badge } from "./ui";
import { Button } from "../ds/core";
import type { TFunction } from "i18next";
import { useLocale } from "../i18n/useLocale";
import { useSwitchLocale } from "../i18n/useSwitchLocale";
import { LIVE_LOCALES, liveLocaleForMarket, type Market } from "../i18n/locales";
import { getLenis } from "../ds/core/lenisBus";
import { postsFor, TAG_TONE, type Post } from "../content/posts";
import { featureLiveForMarket } from "../content/flags";
import { TRIAL_PATH as CTA, LOGIN_URL as LOGIN } from "../config/entrypoints";

/**
 * SiteNav — Done-it's navbar built on the design system: sticky white bar,
 * `Container`, named tokens only, DS `Button`. Hover-morphing mega-menus
 * (Features · Resources), market + language switchers in one dropdown system,
 * and a < 1200px mobile sheet. Self-contained (wraps its own LazyMotion). The
 * lab page at /nav-lab renders this; the live Navbar is untouched until promoted.
 */


// `k` is the i18n key suffix (under the `common:nav.` family); the visible label
// / title / desc are resolved via `t()` at render so they stay translatable.
type NavItem = { k: string; to: string; icon: ComponentType<{ size?: number; strokeWidth?: number; className?: string }>; hasDesc?: boolean };
type NavLinkDef = { k: string; to: string; accent?: boolean };

/* Belgium-market-only routes, gated by `useLocale().market` AND the `law_2027`
   CMS kill-switch (an admin can hide the link without a code change). */
const BELGIUM_ONLY_PATHS = new Set(["/law-2027"]);
const lawShown = (market: Market) => market === "belgium" && featureLiveForMarket(market, "law_2027");

/* Plain center links (mirror the live Navbar). */
const LINKS: NavLinkDef[] = [
  { k: "project", to: "/project-management" },
  { k: "industries", to: "/industries" },
  { k: "law2027", to: "/law-2027", accent: true },
  { k: "pricing", to: "/pricing" },
];

/* Features mega-menu — grounded in the /features taxonomy. One uniform grid of
   cards (not divided into sections). */
const FEATURES: NavItem[] = [
  { k: "timeOps", to: "/features#time", icon: Timer, hasDesc: true },
  { k: "vehicles", to: "/features#vehicles", icon: Car, hasDesc: true },
  { k: "platform", to: "/features#platform", icon: LayoutDashboard, hasDesc: true },
  { k: "overtime", to: "/features#time", icon: Clock, hasDesc: true },
  { k: "leave", to: "/features#time", icon: Plane, hasDesc: true },
];

/* Resources mega-menu — the live nav's four resource destinations. */
const RESOURCES: NavItem[] = [
  { k: "gettingStarted", to: "/getting-started", icon: Rocket, hasDesc: true },
  { k: "blog", to: "/resources", icon: BookOpen, hasDesc: true },
  { k: "trainings", to: "/trainings", icon: PlayCircle, hasDesc: true },
  { k: "faq", to: "/faq", icon: HelpCircle, hasDesc: true },
];

const COUNTRIES: { k: string; market: Market }[] = [
  { k: "belgium", market: "belgium" },
  { k: "international", market: "international" },
];

/** Resolve a NavItem's title (`nav.<k>.title`) and optional desc (`nav.<k>.desc`). */
const itemTitle = (t: TFunction, item: NavItem) => t(`common:nav.${item.k}.title`);
const itemDesc = (t: TFunction, item: NavItem) => (item.hasDesc ? t(`common:nav.${item.k}.desc`) : undefined);
/** Resolve a plain link's label (`nav.<k>`). */
const linkLabel = (t: TFunction, item: NavLinkDef) => t(`common:nav.${item.k}`);
/**
 * Flag + short label are a UI concern, keyed by locale `code` (the dependency-light
 * registry stays free of presentation).
 *
 * The flag identifies the LANGUAGE, because that is the only job it has here: in a
 * language picker three chips that all fly 🇧🇪 tell the reader nothing about which
 * one to click. `fr-be` and `en-be` therefore carry their own language flag.
 *
 * `nl` is the exception, and a deliberate one: within this registry `nl` IS the
 * Belgian locale (`market: "belgium"`) and its copy is Flemish, not Dutch-from-NL,
 * so it flies 🇧🇪 and must never fly 🇳🇱 — a client remark we have already had once.
 * The Netherlands is not a served market; if it ever becomes one it arrives as its
 * own entry and takes 🇳🇱 with it.
 *
 * A code that is not listed falls back to its primary subtag, then a globe, so a
 * newly-added locale still renders without touching this map.
 */
const LOCALE_FLAG: Record<string, string> = {
  nl: "🇧🇪", "fr-be": "🇫🇷", "en-be": "🇬🇧",
  en: "🇬🇧", fr: "🇫🇷", es: "🇪🇸", de: "🇩🇪",
  it: "🇮🇹", pt: "🇵🇹", pl: "🇵🇱", no: "🇳🇴", ro: "🇷🇴", bg: "🇧🇬", tr: "🇹🇷", lt: "🇱🇹",
};
const flagFor = (code: string) =>
  LOCALE_FLAG[code] ?? LOCALE_FLAG[code.split("-")[0]] ?? "🌐";
const shortLabel = (code: string) => code.split("-")[0].toUpperCase();
/** Live locales selectable within a market (drives the language switchers). */
const langsForMarket = (market: Market) => LIVE_LOCALES.filter((l) => l.market === market);

function cn(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

/**
 * Overflow-aware nav collapse (language-agnostic — no per-locale breakpoint). The
 * desktop cluster is laid out `flex-nowrap` inside an `overflow-hidden` measured row,
 * so its `scrollWidth` is the natural content width regardless of the active language;
 * compare it to the available `clientWidth`. When a longer translation (de/pt/…) no
 * longer fits, `fits` flips false and the caller swaps to the hamburger — then the row
 * is hidden but STILL laid out + measured (opacity, not display:none), so the check
 * stays valid and the bar re-expands when there's room again. `dep` (the locale)
 * re-measures on language switch, where the box size doesn't change but the content does.
 */
function useFits<T extends HTMLElement>(dep: unknown) {
  const ref = useRef<T>(null);
  const [fits, setFits] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setFits(el.scrollWidth <= el.clientWidth + 1);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    check();
    return () => ro.disconnect();
  }, [dep]);
  return { ref, fits };
}

// Country + language switchers share one trigger style (same font/size/weight).
const SWITCHER_TRIGGER = "flex items-center gap-1 font-ds text-[14px] font-medium transition-colors";

// Shared hover dropdown — same type as the mega-menu panels (open on hover,
// fade+slide in, identical card styling). The pt-3 wrapper bridges the
// trigger→panel gap so the pointer never leaves before reaching the panel.
//
// The panel is keyed by locale. Both switchers close on pick and immediately
// navigate to another locale, and the pointer is still inside the row, so the
// panel re-opens on top of an exit that is still running — with no key motion
// re-served the CACHED exiting child, leaving the menu on the previous language
// while the trigger beside it already read the new one (DIT-408). A locale-keyed
// child is a different child, so the re-open renders fresh copy.
function HoverDropdown({
  label, open, setOpen, width, children,
}: {
  label: ReactNode;
  open: boolean;
  setOpen: (v: boolean) => void;
  width: string;
  children: ReactNode;
}) {
  const { locale } = useLocale();
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className={cn(SWITCHER_TRIGGER, open ? "text-ink" : "text-ink-soft hover:text-ink")}>
        {label}
        <ChevronDown size={14} strokeWidth={2} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      <AnimatePresence>
        {open && (
          <m.div
            key={locale}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute right-0 top-full z-50 pt-3">
            <div className={cn("overflow-hidden whitespace-normal rounded-2xl border border-line bg-white shadow-[0_12px_40px_rgba(15,23,42,0.12)]", width)}>
              {children}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Dropdown item primitives (DS-styled, internal Links) ---------- */

// Same BentoCard treatment as the Resources blog cards (grey fill, thick white
// border, soft shadow) — kept neutral, no colour.
function GridCard({ item }: { item: NavItem }) {
  const { t } = useLocale();
  const { icon: Icon, to } = item;
  const title = itemTitle(t, item);
  const desc = itemDesc(t, item);
  return (
    <LocaleLink to={to}
      className="group flex h-full flex-col gap-1 rounded-2xl border-4 border-white bg-bg-subtle p-4 shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-shadow hover:shadow-[0_6px_20px_rgba(15,23,42,0.10)]">
      <span className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg bg-white text-primary shadow-sm"><Icon size={18} strokeWidth={2} /></span>
      <p className="text-[14px] font-semibold text-ink">{title}</p>
      {desc && <p className="text-[12.5px] leading-snug text-ink-soft">{desc}</p>}
    </LocaleLink>
  );
}

function SmallItem({ item }: { item: NavItem }) {
  const { t } = useLocale();
  const { icon: Icon, to } = item;
  return (
    <LocaleLink to={to} className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-ink-soft transition-colors hover:bg-bg-subtle hover:text-ink">
      <Icon size={16} strokeWidth={2} className="shrink-0 text-ink-faint" />
      {itemTitle(t, item)}
    </LocaleLink>
  );
}

function LargeItem({ item }: { item: NavItem }) {
  const { t } = useLocale();
  const { icon: Icon, to } = item;
  const title = itemTitle(t, item);
  const desc = itemDesc(t, item);
  return (
    <LocaleLink to={to} className="flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-bg-subtle">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"><Icon size={18} strokeWidth={2} /></span>
      <div>
        <div className="text-[14px] font-semibold text-ink">{title}</div>
        {desc && <div className="mt-0.5 text-[12.5px] leading-snug text-ink-soft">{desc}</div>}
      </div>
    </LocaleLink>
  );
}

// Blog card in our BentoCard style: grey (or blue) fill + thick white border.
// Title only (clamped to two lines); `blue` tone paints the card our classic
// accent blue with white text and a yellow (gold) tag.
function BlogCard({ post, tone = "grey" }: { post: Post; tone?: "grey" | "blue" }) {
  const { t } = useLocale();
  const blue = tone === "blue";
  return (
    <LocaleLink to={`/blog/${post.slug}`}
      className={cn(
        "group flex h-full flex-col gap-3 rounded-2xl border-4 border-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-shadow hover:shadow-[0_6px_20px_rgba(15,23,42,0.10)]",
        blue ? "bg-ds-accent" : "bg-bg-subtle",
      )}>
      {blue ? (
        <span className="inline-flex w-fit items-center rounded-full bg-ds-gold px-3 py-1 text-[12px] font-semibold text-ds-ink">{post.tag}</span>
      ) : (
        <Badge tone={TAG_TONE[post.tag]}>{post.tag}</Badge>
      )}
      <h4 className={cn("line-clamp-2 font-display text-[14.5px] font-semibold leading-snug tracking-tight", blue ? "text-white" : "text-ink")}>{post.title}</h4>
      <p className={cn("line-clamp-2 text-[12.5px] leading-snug", blue ? "text-white/85" : "text-ink-soft")}>{post.lead}</p>
      <span className={cn("mt-auto inline-flex items-center gap-1 text-[12px] font-semibold opacity-0 transition-opacity group-hover:opacity-100", blue ? "text-white" : "text-primary")}>
        {t("common:nav.read")} <ArrowRight size={13} strokeWidth={2} />
      </span>
    </LocaleLink>
  );
}

// Last cell of the Features grid — a "see all" link card, not a feature.
function SeeAllCard() {
  const { t } = useLocale();
  return (
    <LocaleLink to="/features"
      className="group flex h-full flex-col justify-center gap-2 rounded-2xl border-4 border-white bg-bg-subtle p-4 shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-shadow hover:shadow-[0_6px_20px_rgba(15,23,42,0.10)]">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-white shadow-sm">
        <ArrowRight size={18} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5" />
      </span>
      <p className="text-[14px] font-semibold text-ink">{t("common:nav.seeAllFeatures")}</p>
      <p className="text-[12.5px] leading-snug text-ink-soft">{t("common:nav.seeAllFeaturesDesc")}</p>
    </LocaleLink>
  );
}

/* ---------- Mega-menu panels ---------- */

// Two recent articles from the active locale's catalog (Story 4.8: belgium-only
// posts simply aren't in the `en` set, so no runtime market filter is needed).
function recentPosts(locale: string): Post[] {
  return postsFor(locale).slice(0, 2);
}

function FeaturesPanel() {
  return (
    <ul className="grid w-[92vw] max-w-3xl gap-3 p-4 sm:w-[42rem] sm:grid-cols-3">
      {FEATURES.map((item) => (
        <li key={item.k}><GridCard item={item} /></li>
      ))}
      <li><SeeAllCard /></li>
    </ul>
  );
}

function ResourcesPanel() {
  const { locale } = useLocale();
  const posts = recentPosts(locale);
  return (
    <div className="grid w-[92vw] max-w-3xl md:w-[46rem] md:grid-cols-[1fr_.6fr]">
      {/* Two-up only when there are 2 cards — a single post fills the width rather
          than leaving an empty cell in the 2-col grid (markets with <2 posts). */}
      <div className={cn("grid gap-3 p-4 md:border-r md:border-line", posts.length > 1 && "sm:grid-cols-2")}>
        {posts.map((post, i) => (
          <BlogCard key={post.slug} post={post} tone={i === 0 ? "blue" : "grey"} />
        ))}
      </div>
      <ul className="space-y-1 p-4">
        {RESOURCES.map((item) => (
          <li key={item.k}><SmallItem item={item} /></li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- Header items ---------- */

/** Supplied brand lockup (clock mark + wordmark + tagline). Bundled from the
 *  customer's logo pack (DIT-392) rather than pulled off a third-party CDN. */
const BRAND_LOGO = "/done-it-logo.png";

// Logo / brand.
function Brand() {
  const { t } = useLocale();
  return (
    <LocaleLink to="/" className="flex items-center" aria-label={t("common:nav.brandHomeAria")}>
      {/* Fixed width/height so the bar doesn't reflow while the logo loads. */}
      <img
        src={BRAND_LOGO}
        alt="Done-it"
        width={114}
        height={40}
        className="h-10 w-auto"
        decoding="async"
      />
    </LocaleLink>
  );
}

// Mega-menu trigger (Features has a `to`, Resources doesn't). Opens on hover —
// and on click. Hover alone left the Resources button dead: it has no `to` to
// fall back on, so clicking it (or tapping, where hover never fires) did
// nothing at all — which is what the client reported as "this tab does not work".
function NavTrigger({ id, label, to, open, onHover, onToggle }: {
  id: string; label: string; to?: string; open: string | null;
  onHover: (id: string) => void; onToggle: (id: string) => void;
}) {
  const active = open === id;
  const cls = cn("flex items-center gap-1 text-[14px] font-medium transition-colors",
    active ? "text-ink" : "text-ink-soft hover:text-ink");
  const inner = <>{label} <ChevronDown size={14} strokeWidth={2} className={cn("transition-transform", active && "rotate-180")} /></>;
  return (
    <li className="static" onMouseEnter={() => onHover(id)}>
      {to
        ? <LocaleLink to={to} className={cls}>{inner}</LocaleLink>
        : (
          <button type="button" className={cls} aria-expanded={active} aria-haspopup="true"
            onClick={() => onToggle(id)}>
            {inner}
          </button>
        )}
    </li>
  );
}

// Plain center link (Project / Industries / Law 2027 / Pricing).
function NavItemLink({ item, onHover }: { item: NavLinkDef; onHover: () => void }) {
  const { t } = useLocale();
  return (
    <li onMouseEnter={onHover}>
      <LocaleNavLink to={item.to} className={({ isActive }) =>
        cn("flex items-center gap-1.5 text-[14px] font-medium transition-colors",
          isActive ? "text-primary" : item.accent ? "text-ink hover:text-primary" : "text-ink-soft hover:text-ink")}>
        {linkLabel(t, item)}
        {item.accent && <span className="h-1.5 w-1.5 rounded-full bg-accent" />}
      </LocaleNavLink>
    </li>
  );
}

/* ---------- Desktop nav with morphing dropdown viewport ---------- */

function DesktopNav() {
  const { market, t } = useLocale();
  const [open, setOpen] = useState<string | null>(null);
  /**
   * Whether the open panel was opened DELIBERATELY — by a click — rather than by
   * the pointer passing over the trigger.
   *
   * Without the distinction, hover and click cancel each other: moving the mouse
   * to Resources opened the panel, and the click that followed found it open and
   * closed it again, so the menu "does not open on click" (QA 278773, and the
   * client reported the same on 14 Sep). Hover still previews; a click pins the
   * panel open and the next click on the same trigger closes it — as does Escape,
   * or a click anywhere outside the nav.
   */
  const [pinned, setPinned] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const links = LINKS.filter((l) => !BELGIUM_ONLY_PATHS.has(l.to) || lawShown(market));

  const close = () => { setOpen(null); setPinned(false); };
  const hover = (id: string | null) => { if (!pinned) setOpen(id); };
  const toggle = (id: string) => {
    if (open === id && pinned) close();
    else { setOpen(id); setPinned(true); }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    const onDown = (e: MouseEvent) => { if (!navRef.current?.contains(e.target as Node)) close(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [open]);

  return (
    <nav ref={navRef} className="relative flex items-center whitespace-nowrap" onMouseLeave={() => hover(null)}>
      <ul className="flex items-center gap-7">
        <NavTrigger id="features" label={t("common:nav.features")} to="/features" open={open} onHover={hover} onToggle={toggle} />
        {links.map((l) => (
          <NavItemLink key={l.to} item={l} onHover={close} />
        ))}
        <NavTrigger id="resources" label={t("common:nav.resources")} open={open} onHover={hover} onToggle={toggle} />
      </ul>

      {/* Single viewport — stays mounted between tabs (cross-fades content instead
          of closing/reopening). Centering via motion x:"-50%" so motion's own
          transform for `y` doesn't clobber a Tailwind -translate-x-1/2. pt-2
          bridges the trigger→panel gap so the pointer stays in the nav subtree. */}
      <AnimatePresence>
        {open && (
          <m.div
            key="viewport"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            style={{ x: "-50%" }}
            className="absolute left-1/2 top-full z-50 pt-3">
            <div className="overflow-hidden whitespace-normal rounded-2xl border border-line bg-white shadow-[0_12px_40px_rgba(15,23,42,0.12)]">
              <m.div key={open} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.16, ease: "easeOut" }}>
                {open === "features" ? <FeaturesPanel /> : <ResourcesPanel />}
              </m.div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

/* ---------- Right cluster: market + language ---------- */

// Switching market IS a URL change under prefix-all: navigate to that market's
// representative live locale (keeping the current page), no-op if already there.
function useSwitchMarket(): (m: Market) => void {
  const { market } = useLocale();
  const switchLocale = useSwitchLocale();
  return (m: Market) => {
    if (m !== market) switchLocale(liveLocaleForMarket(m));
  };
}

function CountrySwitch() {
  const { market, t } = useLocale();
  const switchMarket = useSwitchMarket();
  const [open, setOpen] = useState(false);
  const current = COUNTRIES.find((c) => c.market === market) ?? COUNTRIES[0];
  return (
    <HoverDropdown label={t(`common:nav.market.${current.k}`)} open={open} setOpen={setOpen} width="w-40">
      {COUNTRIES.map((c) => (
        <button key={c.market} onClick={() => { switchMarket(c.market); setOpen(false); }}
          className={cn("block w-full px-4 py-2.5 text-left text-[14px] transition-colors hover:bg-bg-subtle",
            c.market === market ? "font-semibold text-primary" : "font-medium text-ink-soft hover:text-ink")}>
          {t(`common:nav.market.${c.k}`)}
        </button>
      ))}
    </HoverDropdown>
  );
}

function LanguageDropdown() {
  const { market, locale } = useLocale();
  const switchLocale = useSwitchLocale();
  const langs = langsForMarket(market);
  const [open, setOpen] = useState(false);
  // Registry-driven: only LIVE locales of the active market. Nothing to switch
  // within this market yet (one live language) → render nothing rather than a
  // single dead chip or a list of not-yet-shipped languages.
  if (langs.length < 2) return null;
  const select = (code: string) => { setOpen(false); switchLocale(code); };
  return (
    <HoverDropdown
      label={<span className="flex items-center gap-1.5"><span className="text-[15px] leading-none">{flagFor(locale)}</span>{shortLabel(locale)}</span>}
      open={open} setOpen={setOpen} width="w-56">
      <div className="grid grid-cols-2 gap-0.5 p-1">
        {langs.map((l) => (
          <button key={l.code} onClick={() => select(l.code)}
            className={cn("flex items-center gap-2 rounded-lg px-3 py-2 text-left text-[14px] transition-colors hover:bg-bg-subtle",
              l.code === locale ? "font-semibold text-primary" : "font-medium text-ink-soft hover:text-ink")}>
            <span className="text-[15px] leading-none">{flagFor(l.code)}</span>
            {shortLabel(l.code)}
          </button>
        ))}
      </div>
    </HoverDropdown>
  );
}

/* ---------- Mobile sheet items ---------- */

// Law 2027 — grey BentoCard-style card.
function LawCard({ item, onClick }: { item: NavLinkDef; onClick?: () => void }) {
  const { t } = useLocale();
  return (
    <LocaleLink to={item.to} onClick={onClick}
      className="flex items-center gap-3 rounded-2xl border-4 border-white bg-bg-subtle p-3.5 shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-shadow hover:shadow-[0_6px_20px_rgba(15,23,42,0.10)]">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"><ShieldCheck size={18} strokeWidth={2} /></span>
      <div>
        <div className="text-[14px] font-semibold text-ink">{linkLabel(t, item)}</div>
        <div className="text-[12.5px] leading-snug text-ink-soft">{t("common:nav.lawCardDesc")}</div>
      </div>
    </LocaleLink>
  );
}

// Plain link row in the sheet.
function MobileLinkRow({ to, label, onClick }: { to: string; label: string; onClick: () => void }) {
  return (
    <LocaleLink to={to} onClick={onClick}
      className="block border-b border-line py-3.5 text-[15px] font-semibold text-ink">
      {label}
    </LocaleLink>
  );
}

// Collapsible accordion section (controlled by the sheet).
function MobileAccordion({ id, name, open, onToggle, children }: {
  id: string; name: string; open: boolean; onToggle: (id: string) => void; children: ReactNode;
}) {
  return (
    <div className="border-b border-line">
      <button onClick={() => onToggle(id)}
        className="flex w-full items-center justify-between py-3.5 text-[15px] font-semibold text-ink">
        {name}
        <ChevronDown size={16} strokeWidth={2} className={cn("text-ink-faint transition-transform", open && "rotate-180")} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <m.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }} className="overflow-hidden">
            {children}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Full-width region segmented switcher (white pill slides between markets).
function RegionSwitch() {
  const { market, t } = useLocale();
  const switchMarket = useSwitchMarket();
  return (
    <div className="relative flex w-full rounded-xl bg-bg-subtle p-1">
      <m.div aria-hidden className="absolute bottom-1 left-1 top-1 w-[calc(50%-4px)] rounded-lg bg-white shadow-sm"
        animate={{ x: market === COUNTRIES[0].market ? "0%" : "100%" }}
        transition={{ type: "spring", stiffness: 420, damping: 36 }} />
      {COUNTRIES.map((c) => (
        <button key={c.market} onClick={() => switchMarket(c.market)}
          className={cn("relative z-10 flex-1 rounded-lg py-2 text-center text-[14px] font-medium transition-colors",
            market === c.market ? "text-primary" : "text-ink-soft hover:text-ink")}>
          {t(`common:nav.market.${c.k}`)}
        </button>
      ))}
    </div>
  );
}

// Language chips with flags on a grey panel — registry-driven (live locales only).
function LanguageChips() {
  const { market, locale, t } = useLocale();
  const switchLocale = useSwitchLocale();
  const langs = langsForMarket(market);
  // One live language in this market → nothing to switch; omit the panel.
  if (langs.length < 2) return null;
  return (
    <div className="rounded-xl bg-bg-subtle p-3">
      <span className="text-[13px] font-semibold text-ink-soft">{t("common:nav.language")}</span>
      <div className="relative mt-2">
        <div className="flex gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {langs.map((l) => (
            <button key={l.code} onClick={() => switchLocale(l.code)}
              className={cn("flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-[14px] font-medium transition-colors",
                l.code === locale ? "bg-white text-primary shadow-sm" : "text-ink-soft hover:text-ink")}>
              <span className="text-[15px] leading-none">{flagFor(l.code)}</span>{shortLabel(l.code)}
            </button>
          ))}
        </div>
        {/* Right-edge fade — hints there's more to scroll (only when it overflows). */}
        {langs.length > 6 && (
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-bg-subtle to-transparent" />
        )}
      </div>
    </div>
  );
}

/* ---------- Mobile sheet ---------- */

function MobileNav() {
  const { market, t } = useLocale();
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<string | null>(null);
  // Portal the drawer to <body> so it escapes the page's stacking contexts (on the
  // home page the nav lives inside <main className="z-10 overflow-clip">, which
  // otherwise traps the drawer BELOW the chat button). Client-only: stays out of the
  // prerendered HTML, which never has the menu open anyway.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const plainLinks = LINKS.filter((l) => !l.accent); // Project, Industries, Pricing
  const law = LINKS.find((l) => l.accent);
  const showLaw = law && lawShown(market);
  const close = () => setOpen(false);
  const toggle = (id: string) => setSection((s) => (s === id ? null : id));

  // Lock the page scroll behind the open menu. Lenis (smooth scroll) hijacks the
  // wheel and bypasses overflow:hidden, so pause it; and pin <body> in place
  // (position:fixed) to also stop native touch scroll. Restore both on close.
  useEffect(() => {
    if (!open) return;
    const lenis = getLenis();
    lenis?.stop();
    const { body } = document;
    const scrollY = window.scrollY;
    const prev = { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      Object.assign(body.style, prev);
      window.scrollTo(0, scrollY);
      lenis?.start();
    };
  }, [open]);

  return (
    <>
      <button className="text-ink" aria-label={t("common:nav.openMenu")} onClick={() => setOpen(true)}>
        <Menu size={22} />
      </button>

      {mounted && createPortal(
        <AnimatePresence>
        {open && (
          <>
            <m.div className="fixed inset-0 z-[60] bg-black/55"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.34, ease: "easeOut" }} onClick={close} />
            <m.div
              className="fixed inset-y-0 right-0 z-[70] flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(15,23,42,0.12)] md:max-w-sm md:border-l md:border-line"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.34, ease: [0.32, 0.72, 0, 1] }}>
              <div className="flex h-16 items-center justify-between border-b border-line px-5">
                <span className="font-display text-[17px] font-bold text-ink">{t("common:nav.menu")}</span>
                <button className="text-ink-soft hover:text-ink" onClick={close} aria-label={t("common:nav.closeMenu")}><X size={22} /></button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4">
                {/* TOP — Law 2027 card + branded CTAs */}
                {showLaw && law && <LawCard item={law} onClick={close} />}
                <div className={cn("flex gap-3", showLaw && "mt-3")}>
                  <Button to={LOGIN} variant="outline" className="shrink-0">{t("common:nav.login")}</Button>
                  <Button to={CTA} variant="solid" className="flex-1 justify-between">{t("common:nav.startTrial")}</Button>
                </div>

                {/* MENU */}
                <div className="mt-5">
                  {/* Features — accordion on phone, single redirect link on tablet */}
                  <div className="md:hidden">
                    <MobileAccordion id="features" name={t("common:nav.features")} open={section === "features"} onToggle={toggle}>
                      <ul className="grid gap-0.5 pb-3">
                        {FEATURES.map((item) => (
                          <li key={item.k} onClick={close}><LargeItem item={item} /></li>
                        ))}
                        <li onClick={close}>
                          <LocaleLink to="/features" className="flex items-center gap-1.5 rounded-xl p-3 text-[14px] font-semibold text-primary transition-colors hover:bg-bg-subtle">
                            {t("common:nav.seeAllFeatures")} <ArrowRight size={15} strokeWidth={2} />
                          </LocaleLink>
                        </li>
                      </ul>
                    </MobileAccordion>
                  </div>
                  <LocaleLink to="/features" onClick={close}
                    className="hidden border-b border-line py-3.5 text-[15px] font-semibold text-ink md:block">
                    {t("common:nav.features")}
                  </LocaleLink>

                  {plainLinks.map((l) => (
                    <MobileLinkRow key={l.to} to={l.to} label={linkLabel(t, l)} onClick={close} />
                  ))}

                  {/* Resources — accordion, consistent with the other menu rows */}
                  <MobileAccordion id="resources" name={t("common:nav.resources")} open={section === "resources"} onToggle={toggle}>
                    <ul className="grid gap-0.5 pb-3">
                      {RESOURCES.map((item) => (
                        <li key={item.k} onClick={close}><LargeItem item={item} /></li>
                      ))}
                    </ul>
                  </MobileAccordion>
                </div>
              </div>

              {/* Footer — region switcher + language chips */}
              <div className="space-y-3 border-t border-line p-4">
                <RegionSwitch />
                <LanguageChips />
              </div>
            </m.div>
          </>
        )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}

/* ---------- Site navigation ---------- */

export function SiteNav() {
  const { t, locale } = useLocale();
  // Overflow-aware collapse: re-measures on locale change (longer translations push
  // the bar wider) — no per-language breakpoint.
  const { ref, fits } = useFits<HTMLDivElement>(locale);
  return (
    <LazyMotion features={domAnimation}>
      <header className="sticky top-0 z-50 border-b border-line bg-white font-ds">
        {/* The nav uses a WIDER max-width than the 1200px content sections so the
            primary languages render the full bar (the dense bar + long translations
            need the room); content below stays at max-w-content. The overflow-collapse
            above still protects any language that exceeds even this. */}
        <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center gap-10 px-4 md:px-10">
          <Brand />

          {/* The full desktop cluster (center links → right cluster), laid out
              flex-nowrap and clipped so its scrollWidth is the natural content width.
              When it doesn't fit it's hidden via opacity (NOT removed) so it stays
              measured and the bar can re-expand. */}
          <div
            ref={ref}
            aria-hidden={!fits}
            className={cn(
              // `ml-auto` (not a flex-1 spacer) pushes the right cluster to the end
              // WITHOUT adding measurable width/gaps, so scrollWidth is the true
              // content width (DesktopNav + right cluster) for an honest fit check.
              // `overflow-hidden` ONLY when collapsed — otherwise it clips the
              // hover dropdowns (mega-menus + switchers) that drop below the bar.
              // The fit check (scrollWidth vs clientWidth) works either way because
              // the row is flex-1 (width set by flex, not content).
              "flex min-w-0 flex-1 items-center",
              !fits && "overflow-hidden pointer-events-none opacity-0",
            )}
          >
            <DesktopNav />
            <div className="ml-auto flex items-center gap-4 whitespace-nowrap">
              <CountrySwitch />
              <LanguageDropdown />
              <div className="flex items-center gap-3">
                <Button to={LOGIN} variant="outline" className="whitespace-nowrap">{t("common:nav.login")}</Button>
                <Button to={CTA} variant="solid" className="whitespace-nowrap">{t("common:nav.startTrial")}</Button>
              </div>
            </div>
          </div>

          {/* Hamburger — shown only when the desktop cluster overflows (any language). */}
          <div className={cn("shrink-0", fits && "hidden")}>
            <MobileNav />
          </div>
        </div>
      </header>
    </LazyMotion>
  );
}
